import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "./AuthContext";
import API from "../config/Api";
import { showToast } from "../components/Toast";
import { playNotificationTone } from "../utils/notificationSound";
import {
    enablePushSubscription,
    getPushSubscription,
    registerExistingPushSubscription,
    supportsWebPush,
    unregisterCurrentPushSubscription,
} from "../utils/pushNotifications";

const defaultValue = {
    notifications: [], unreadCount: 0, loading: false, hasMore: false,
    pushState: "unsupported", pushAvailable: false,
    refresh: async () => {}, loadMore: async () => {}, markRead: async () => {}, markAllRead: async () => {},
    enablePush: async () => {}, disablePush: async () => {},
};

export const NotificationContext = createContext(defaultValue);
export const useNotifications = () => useContext(NotificationContext);

const streamUrl = () => {
    const base = String(API.defaults.baseURL || "/api").replace(/\/$/, "");
    return new URL(`${base}/notifications/stream`, window.location.origin).toString();
};

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const normalizeNotification = (notification) => {
    if (!notification) return null;
    const linkUrl = notification.linkUrl || notification.url;
    return { ...notification, linkUrl };
};

const consumeEventStream = async (response, onNotification) => {
    if (!response.body?.getReader) throw new Error("Flux temps reel non disponible");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
        let boundary = buffer.indexOf("\n\n");
        while (boundary >= 0) {
            const block = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf("\n\n");
            const eventName = block.split("\n").find((line) => line.startsWith("event:"))?.slice(6).trim();
            const data = block.split("\n").filter((line) => line.startsWith("data:"))
                .map((line) => line.slice(5).trimStart()).join("\n");
            if (eventName === "notification" && data) {
                try { onNotification(JSON.parse(data)); } catch (error) { /* Ignore a malformed event. */ }
            }
        }
    }
};

export const NotificationProvider = ({ children }) => {
    const { user, token } = useContext(AuthContext);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [pushState, setPushState] = useState("unsupported");
    const [pushAvailable, setPushAvailable] = useState(false);
    const [pushKey, setPushKey] = useState("");
    const seenIds = useRef(new Set());
    const activeIdentity = useRef(null);

    const mergeNotifications = useCallback((incoming, { announce = false, incrementUnread = false } = {}) => {
        const rows = (Array.isArray(incoming) ? incoming : [incoming]).map(normalizeNotification).filter(Boolean);
        const trulyNew = rows.filter((row) => row?.id != null && !seenIds.current.has(String(row.id)));
        rows.forEach((row) => row?.id != null && seenIds.current.add(String(row.id)));
        setNotifications((current) => {
            const byId = new Map(current.map((row) => [String(row.id), row]));
            rows.filter(Boolean).forEach((row) => byId.set(String(row.id), { ...byId.get(String(row.id)), ...row }));
            return [...byId.values()].sort((left, right) => Number(right.id) - Number(left.id)).slice(0, 50);
        });
        if (trulyNew.length && incrementUnread) {
            setUnreadCount((current) => current + trulyNew.filter((row) => !row.read).length);
        }
        if (trulyNew.length && announce) {
            const latest = trulyNew[0];
            playNotificationTone();
            showToast({
                title: latest.title || "Nouvelle notification",
                message: latest.message || "Une nouvelle action vous attend.",
                type: ["ERROR", "DANGER"].includes(latest.severity) ? "error" : latest.severity === "WARNING" ? "warning" : "info",
                duration: 5000,
            });
        }
    }, []);

    const refresh = useCallback(async ({ silent = false } = {}) => {
        if (!user || !token) return;
        if (!silent) setLoading(true);
        try {
            const response = await API.get("/notifications", { params: { limit: 20 } });
            mergeNotifications(response.data?.notifications || [], false);
            setUnreadCount(Number(response.data?.unreadCount || 0));
            setHasMore(Boolean(response.data?.hasMore));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [mergeNotifications, token, user]);

    const loadMore = useCallback(async () => {
        if (!hasMore || loading || notifications.length === 0) return;
        setLoading(true);
        try {
            const beforeId = notifications[notifications.length - 1]?.id;
            const response = await API.get("/notifications", { params: { limit: 20, beforeId } });
            mergeNotifications(response.data?.notifications || [], false);
            setHasMore(Boolean(response.data?.hasMore));
        } finally {
            setLoading(false);
        }
    }, [hasMore, loading, mergeNotifications, notifications]);

    const receiveLive = useCallback((notification) => {
        if (!notification?.id) return;
        const isNewToSession = !seenIds.current.has(String(notification.id));
        mergeNotifications(notification, {
            announce: document.visibilityState === "visible",
            incrementUnread: true,
        });
        if (isNewToSession) {
            window.dispatchEvent(new CustomEvent("soma:notification", { detail: notification }));
        }
    }, [mergeNotifications]);

    const markRead = useCallback(async (notification) => {
        if (!notification?.id || notification.read) return;
        setNotifications((current) => current.map((row) => row.id === notification.id ? { ...row, read: true } : row));
        setUnreadCount((current) => Math.max(0, current - 1));
        try {
            await API.post(`/notifications/${notification.id}/read`);
        } catch (error) {
            refresh({ silent: true }).catch(() => {});
        }
    }, [refresh]);

    const markAllRead = useCallback(async () => {
        const previous = notifications;
        setNotifications((current) => current.map((row) => ({ ...row, read: true })));
        setUnreadCount(0);
        try {
            await API.post("/notifications/read-all");
        } catch (error) {
            setNotifications(previous);
            refresh({ silent: true }).catch(() => {});
        }
    }, [notifications, refresh]);

    const inspectPush = useCallback(async () => {
        if (!supportsWebPush()) {
            setPushState("unsupported");
            return;
        }
        if (window.Notification.permission === "denied") {
            setPushState("blocked");
            return;
        }
        const subscription = await getPushSubscription();
        setPushState(subscription ? "enabled" : "available");
        if (subscription) await registerExistingPushSubscription();
    }, []);

    const enablePush = useCallback(async () => {
        if (!pushAvailable || !pushKey) throw new Error("Les notifications appareil ne sont pas configurees sur le serveur.");
        await enablePushSubscription(pushKey);
        setPushState("enabled");
        showToast({ title: "Notifications activees", message: "Cet appareil recevra les nouvelles actions SOMA MASTER.", type: "success" });
    }, [pushAvailable, pushKey]);

    const disablePush = useCallback(async () => {
        await unregisterCurrentPushSubscription();
        setPushState("available");
        showToast({ title: "Notifications desactivees", message: "Cet appareil ne recevra plus d'alertes systeme.", type: "info" });
    }, []);

    useEffect(() => {
        const identity = user ? String(user.Id ?? user.id ?? user.email ?? "authenticated") : null;
        if (identity !== activeIdentity.current) {
            activeIdentity.current = identity;
            seenIds.current.clear();
            setNotifications([]);
            setUnreadCount(0);
            setHasMore(false);
            setPushAvailable(false);
            setPushKey("");
        }
        if (!user || !token) {
            setPushState("unsupported");
            return undefined;
        }
        let stopped = false;
        let controller;
        let pollTimer;

        const connect = async () => {
            let retryDelay = 2000;
            while (!stopped) {
                controller = new AbortController();
                try {
                    const response = await window.fetch(streamUrl(), {
                        headers: { Accept: "text/event-stream", Authorization: `Bearer ${token}` },
                        cache: "no-store",
                        signal: controller.signal,
                    });
                    if (!response.ok) throw new Error(`Flux notifications indisponible (${response.status})`);
                    retryDelay = 2000;
                    await consumeEventStream(response, receiveLive);
                } catch (error) {
                    if (stopped || error?.name === "AbortError") break;
                }
                if (!stopped) {
                    await wait(retryDelay);
                    retryDelay = Math.min(30000, retryDelay * 2);
                }
            }
        };

        const initialize = async () => {
            try { await refresh(); } catch (error) { /* The polling fallback will retry. */ }
            try {
                const config = await API.get("/notifications/push/config");
                const enabled = Boolean(config.data?.enabled && config.data?.applicationServerKey);
                setPushAvailable(enabled);
                setPushKey(config.data?.applicationServerKey || "");
                if (enabled) {
                    try { await inspectPush(); } catch (error) { setPushState("available"); }
                } else setPushState(supportsWebPush() ? "server-disabled" : "unsupported");
            } catch (error) {
                setPushAvailable(false);
                setPushState(supportsWebPush() ? "server-disabled" : "unsupported");
            }
            if (!stopped) connect();
            pollTimer = window.setInterval(() => refresh({ silent: true }).catch(() => {}), 90000);
        };
        initialize();

        const handleWorkerMessage = (event) => {
            if (event.data?.type === "SOMA_NOTIFICATION") receiveLive(event.data.notification);
        };
        navigator.serviceWorker?.addEventListener?.("message", handleWorkerMessage);
        return () => {
            stopped = true;
            controller?.abort();
            if (pollTimer) window.clearInterval(pollTimer);
            navigator.serviceWorker?.removeEventListener?.("message", handleWorkerMessage);
        };
    }, [inspectPush, receiveLive, refresh, token, user]);

    return <NotificationContext.Provider value={{
        notifications, unreadCount, loading, hasMore, pushState, pushAvailable,
        refresh, loadMore, markRead, markAllRead, enablePush, disablePush,
    }}>{children}</NotificationContext.Provider>;
};
